import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyJobs } from "../../hooks/useCompanyJobs";
import {
  companyJobToOpenRole,
  getCompanyJobsStats,
  isCompanyPageRole,
} from "../../lib/company-jobs";
import { createNewBlock } from "../../lib/public-profile-builder";
import { usePublicProfileBuilderModel } from "./PublicProfileBuilderProvider";

// The pure block-shape helpers live in `lib/public-profile-open-roles` so they
// can be unit tested without the expo hook chain this module pulls in.
// Re-exported here because the profile editors already import them from this
// path.
export {
  getOpenRolesLayout,
  getOpenRolesThumbnail,
  isTalentPoolEnabled,
  withOpenRolesLayout,
  withOpenRolesThumbnail,
  withTalentPoolEnabled,
} from "../../lib/public-profile-open-roles";

export function useCompanyOpenRoles() {
  const { t } = useTranslation("common");
  const { blocks, setBlocks, routeMode } = usePublicProfileBuilderModel();
  const isCompanyMode = routeMode === "company";
  const { jobs, total, isLoading, error, refetch } = useCompanyJobs(undefined, {
    enabled: isCompanyMode,
  });

  const openRolesBlock = useMemo(
    () => blocks.find((block) => block.type === "openroles") ?? null,
    [blocks],
  );
  // The preview must show what the public page will show, not every live role
  // (PRD §7.5). Filtering on status alone listed board-only, hidden and
  // confidential roles here while the public page correctly left them out, so
  // the employer's own preview asserted the opposite of what shipped - which
  // is how a confidential role came to look like it was on the company page.
  const companyPageJobs = useMemo(() => jobs.filter(isCompanyPageRole), [jobs]);
  const liveRoles = useMemo(
    () => companyPageJobs.map((job) => companyJobToOpenRole(job, t)),
    [companyPageJobs, t],
  );
  const stats = useMemo(() => getCompanyJobsStats(jobs), [jobs]);
  const shouldAddOpenRolesBlock =
    isCompanyMode && liveRoles.length > 0 && !openRolesBlock;
  const shouldEnableOpenRolesBlock =
    isCompanyMode && liveRoles.length > 0 && openRolesBlock?.enabled === false;

  useEffect(() => {
    if (!shouldAddOpenRolesBlock && !shouldEnableOpenRolesBlock) {
      return;
    }

    setBlocks((previous) => {
      const existingIndex = previous.findIndex(
        (block) => block.type === "openroles",
      );

      if (existingIndex === -1) {
        if (!shouldAddOpenRolesBlock) {
          return previous;
        }

        return [
          ...previous,
          {
            ...createNewBlock("openroles"),
            order: previous.length,
          },
        ];
      }

      const existing = previous[existingIndex];
      if (!shouldEnableOpenRolesBlock || existing.enabled) {
        return previous;
      }

      const next = [...previous];
      next[existingIndex] = {
        ...existing,
        enabled: true,
      };
      return next;
    });
  }, [setBlocks, shouldAddOpenRolesBlock, shouldEnableOpenRolesBlock]);

  return {
    isCompanyMode,
    openRolesBlock,
    jobs,
    companyPageJobs,
    liveRoles,
    total,
    stats,
    isLoading,
    error,
    refetch,
  };
}
